import trace from '~/lib/trace';
import {useCallback, useEffect, useRef} from 'react';
import moment from 'moment';

import {service_request as serviceRequestApi} from '~/api/private';
import {useDispatch, useSelector} from '~/lib/hooks';
import {throttle} from '~/lib/utils';
import {
  serviceRequestLastFetch,
  setAppIsLoading,
  setAppDoneLoading,
} from '~/actions';
import {DataProvider} from 'recyclerlistview';
import {
  createCalendarSections,
  getDateIndexByKey,
  getOffsetForDate,
} from '../helper';

const expensiveFetch = throttle(async () => {
  const {result} = await serviceRequestApi.requests();
  return result;
});

export const useCalendarData = ({
  me,
  isFocused,
  setVisibleIndex,
  setListOffset,
  forceRerender,
  weekListViewRef,
  listViewRef,
}) => {
  const dispatch = useDispatch();

  const forceFetch = useSelector((state) => state.serviceRequestInsert);
  const forceRefresh = useSelector((state) => state.serviceRequestUpdate);
  const lastFetch = useSelector((state) => state.serviceRequestLastFetch);

  const calendar_ctx = useRef();
  calendar_ctx.current = useSelector((state) => state.calendar_ctx);

  const requests = useRef([]);
  const dates = useRef([]);
  const sections = useRef(null);

  const weekDataProvider = useRef(new DataProvider((r1, r2) => r1 !== r2));
  const listDataProvider = useRef(new DataProvider((r1, r2) => r1 !== r2));

  const createSections = useCallback(() => {
    const {sections: nextSections, dates: nextDates} = createCalendarSections(
      requests.current,
      moment(),
    );

    sections.current = nextSections;
    dates.current = nextDates;

    listDataProvider.current = listDataProvider.current.cloneWithRows(
      sections.current,
    );
    weekDataProvider.current = weekDataProvider.current.cloneWithRows(
      dates.current,
    );
  }, []);

  useEffect(() => {
    dispatch(setAppIsLoading());

    const fetch = async () => {
      requests.current = await expensiveFetch();
      createSections();

      const key = moment().format('YYYY-MM-DD');
      const todayOffset = getOffsetForDate(
        sections.current,
        key,
        calendar_ctx?.current?.offset,
      );

      setListOffset(todayOffset);

      const dateIndex = getDateIndexByKey(dates.current, key);
      setVisibleIndex(dateIndex);

      dispatch(setAppDoneLoading());
      forceRerender();
    };

    fetch().catch(trace);

    return () => {
      dispatch(setAppDoneLoading());
    };
  }, [
    me,
    dispatch,
    calendar_ctx,
    forceRerender,
    createSections,
    setListOffset,
    setVisibleIndex,
  ]);

  useEffect(() => {
    const fetch = async () => {
      requests.current = await expensiveFetch();
      createSections();

      forceRerender();
    };

    fetch().catch(trace);
  }, [calendar_ctx, isFocused, forceFetch, createSections, forceRerender]);

  useEffect(() => {
    let changed = false;

    const data = requests.current ?? [];
    for (const request of data) {
      const request_id = request.id;

      const last_fetch_date = moment(lastFetch[request_id] ?? new Date(0));
      const update_date = moment(
        forceRefresh[request_id]?.timestamp ?? new Date(0),
      );

      if (update_date.isAfter(last_fetch_date, 'seconds')) {
        const {status, rob_status, rob_invoicing_status} =
          forceRefresh[request_id];

        if (status) {
          request.status = status;
          request.rob_status = rob_status;
          request.rob_invoicing_status = rob_invoicing_status;

          // Update the last fetch date.
          // Conceptiually not very elegant, better to really do a fetch. But for now this is acceptable.
          dispatch(serviceRequestLastFetch(request_id));
          changed = true;

          console.log(
            'Refreshing service_request',
            request,
            last_fetch_date,
            update_date,
          );
        }
      }
    }

    if (changed) {
      requests.current = data;
      createSections();

      forceRerender();
    }
  }, [
    requests,
    forceRefresh,
    lastFetch,
    dispatch,
    forceRerender,
    createSections,
  ]);

  return {
    requests,
    dates,
    sections,
    listDataProvider,
    weekDataProvider,
    createSections,
    calendar_ctx,
  };
};
